import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, CardSection, Checkbox, Field, Select, Spinner, useToast } from "@/components/ui";
import { adminApi, membershipsApi } from "@/lib/api/endpoints";
import type { EventMembership, EventPermission, User } from "@/lib/types/api";
import { PERMISSION_LABELS } from "@/lib/utils/permissions";
import { IconPlus, IconTrash } from "../components/icons";

/**
 * Who can administer this event, and what they're allowed to do here.
 *
 * Lives on the event rather than on the person because that's the question an
 * operator actually asks — "who can touch the Heineken activation?" — and
 * because permissions are granted per event: the same colleague can be trusted
 * to restyle one booth and only pull prints on another.
 *
 * Only org admins and superadmins ever see this tab. An event admin cannot hand
 * out access, including on events they run, so that there is always exactly one
 * person who can answer the question above.
 */
export function AccessTab({ eventId }: { eventId: number }) {
  const toast = useToast();

  const [memberships, setMemberships] = useState<EventMembership[] | null>(null);
  const [candidates, setCandidates] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<string>("");
  const [newPermissions, setNewPermissions] = useState<EventPermission[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [access, users] = await Promise.all([
        membershipsApi.list(eventId),
        adminApi.users.list(),
      ]);
      setMemberships(access.memberships);
      setNewPermissions((current) =>
        current.length > 0 ? current : access.default_permissions,
      );
      // Only event admins can be assigned. Org admins already reach every
      // event, so offering them here would only produce a confusing error.
      setCandidates(users.filter((user) => user.role === "event_admin"));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load access");
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!selectedUser) return;
    setBusy(true);
    try {
      await membershipsApi.add(eventId, Number(selectedUser), newPermissions);
      setSelectedUser("");
      await load();
      toast.success("Access granted");
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "Could not grant access");
    } finally {
      setBusy(false);
    }
  }

  async function togglePermission(membership: EventMembership, permission: EventPermission) {
    const next = membership.permissions.includes(permission)
      ? membership.permissions.filter((item) => item !== permission)
      : [...membership.permissions, permission];

    // Optimistic: a checkbox that waits for a round trip feels broken.
    setMemberships((current) =>
      (current ?? []).map((item) =>
        item.id === membership.id ? { ...item, permissions: next } : item,
      ),
    );

    try {
      await membershipsApi.setPermissions(eventId, membership.id, next);
    } catch (caught) {
      // Put it back — the server is the truth, and silently keeping a checkbox
      // ticked would tell the operator someone has access they don't have.
      await load();
      toast.error(caught instanceof Error ? caught.message : "Could not save that change");
    }
  }

  async function revoke(membership: EventMembership) {
    setBusy(true);
    try {
      await membershipsApi.remove(eventId, membership.id);
      await load();
      toast.success(`${membership.email} can no longer access this event`);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "Could not remove access");
    } finally {
      setBusy(false);
    }
  }

  if (memberships === null) {
    return (
      <div className="grid place-items-center py-16">
        {error
          ? <p className="text-sm text-danger">{error}</p>
          : <Spinner size={22} className="text-accent" />}
      </div>
    );
  }

  const unassigned = candidates.filter(
    (user) => !memberships.some((membership) => membership.user_id === user.id),
  );

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        title="Event admins"
        description="People who can work on this event only. Everything they can do is set per person, here."
        >
        <Card className="p-5">
          {memberships.length === 0 ? (
            <p className="text-sm text-haze-400">
              Nobody is assigned yet. Only your organization admins can reach this event.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {memberships.map((membership) => (
                <div key={membership.id} className="rounded-xl border border-ink-600 bg-ink-800/50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{membership.email}</span>
                      {membership.invitation_pending && (
                        // Legacy: created by the old invite flow, whose email
                        // never sent. They can't sign in until an org admin
                        // sets them a password on the Team page.
                        <Badge tone="warn">Needs a password</Badge>
                      )}
                      {membership.permissions.length === 0 && (
                        <Badge tone="warn">No access</Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => void revoke(membership)}
                      disabled={busy}
                      className="text-haze-400 hover:text-danger"
                    >
                      <IconTrash size={14} /> Remove
                    </Button>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {(Object.keys(PERMISSION_LABELS) as EventPermission[]).map((permission) => (
                      <Checkbox
                        key={permission}
                        label={PERMISSION_LABELS[permission].label}
                        hint={PERMISSION_LABELS[permission].hint}
                        checked={membership.permissions.includes(permission)}
                        onChange={() => void togglePermission(membership, permission)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </CardSection>

      <CardSection
        title="Give someone access"
        description="Only people whose role is already Event admin appear here. Add them on the Team page first."
        >
        <Card className="p-5">
          {unassigned.length === 0 ? (
            <p className="text-sm text-haze-400">
              No event admins left to assign. Add one from the Team page.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              <Field label="Person">
                <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                  <option value="">Choose someone…</option>
                  {unassigned.map((user) => (
                    <option key={user.id} value={String(user.id)}>{user.email}</option>
                  ))}
                </Select>
              </Field>

              <div className="grid gap-2 sm:grid-cols-2">
                {(Object.keys(PERMISSION_LABELS) as EventPermission[]).map((permission) => (
                  <Checkbox
                    key={permission}
                    label={PERMISSION_LABELS[permission].label}
                    hint={PERMISSION_LABELS[permission].hint}
                    checked={newPermissions.includes(permission)}
                    onChange={(checked) =>
                      setNewPermissions((current) =>
                        checked
                          ? [...current, permission]
                          : current.filter((item) => item !== permission),
                      )
                    }
                  />
                ))}
              </div>

              <div>
                <Button onClick={() => void add()} disabled={!selectedUser || busy}>
                  <IconPlus size={14} /> Grant access
                </Button>
              </div>
            </div>
          )}
        </Card>
      </CardSection>
    </div>
  );
}
